import React, { useState } from "react";
import { plannerAPI } from "../../api/client";
import PlannerContextMenu from "./PlannerContextMenu";
import RecurringScheduleConfirmModal from "./RecurringScheduleConfirmModal";
import { formatLocalDate } from "../../utils/dateUtils";
import useStatusColors from "../../hooks/useStatusColors";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const getWeekStart = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getUserRoleLabel = (user) => {
  if (!user?.role) return "";
  if (typeof user.role === "string") return user.role;
  return user.role?.name || "";
};

export default function PlannerWeekView({
  focusDate,
  data,
  isTeamView,
  isAdmin,
  onDayDoubleClick,
  onRefresh,
}) {
  const [contextMenu, setContextMenu] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const { getStatusStyle } = useStatusColors();

  const weekStart = getWeekStart(focusDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
  const today = formatLocalDate(new Date());

  const handleContextMenu = (e, dateStr, dayInfo, user) => {
    e.preventDefault();
    setContextMenu({
      x: Math.min(e.clientX, window.innerWidth - 220),
      y: Math.min(e.clientY, window.innerHeight - 300),
      dateStr,
      dayInfo,
      user,
    });
  };

  const handleContextAction = async (action, { dateStr, note }) => {
    const userId = contextMenu?.user?.userId || contextMenu?.user?.id;

    switch (action) {
      case "MARK_LEAVE":
        if (userId) {
          await plannerAPI.upsertDayOverride(userId, {
            date: dateStr,
            type: "OFF",
            note: String(note || "").trim() || "Leave",
          });
          await onRefresh();
        }
        break;
      case "MARK_HALF_DAY":
        if (userId) {
          await plannerAPI.upsertDayOverride(userId, {
            date: dateStr,
            type: "HALF_DAY",
          });
          await onRefresh();
        }
        break;
      case "MARK_ONE_OFF_WORKING":
        if (userId) {
          await plannerAPI.upsertDayOverride(userId, {
            date: dateStr,
            type: "WORKING",
            note: "One-off working day",
          });
          await onRefresh();
        }
        break;
      case "RESTORE_DAY":
      case "REMOVE_OVERRIDE":
        if (userId) {
          await plannerAPI.removeDayOverride(userId, dateStr);
          await onRefresh();
        }
        break;
      case "MARK_RECURRING_OFF":
        if (contextMenu?.user) {
          setConfirmModal({
            type: "REMOVE_WORKING_DAY",
            dateStr,
            user: contextMenu.user,
            currentWorkingDays: contextMenu.user.workingDays || [],
          });
        }
        break;
      case "VIEW_TASKS":
        onDayDoubleClick(dateStr);
        break;
      default:
        break;
    }

    setContextMenu(null);
  };

  if (isTeamView) {
    return (
      <div className="week-view team-week">
        <div className="week-table-wrap">
          <table className="week-table">
            <thead>
              <tr>
                <th className="week-user-col">User</th>
                {weekDays.map((d, i) => {
                  const dateStr = formatLocalDate(d);
                  const isToday = dateStr === today;
                  return (
                    <th
                      key={dateStr}
                      className={`week-day-col ${isToday ? "today" : ""}`}
                      onDoubleClick={() => onDayDoubleClick(dateStr)}
                      title="Double-click to see day view"
                    >
                      <span className="week-day-name">{DAY_NAMES[i]}</span>
                      <span className="week-day-num">{d.getDate()}</span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {data?.users?.map((user) => (
                <tr key={user.userId} className="week-user-row">
                  <td className="week-user-cell">
                    <div className="week-user-avatar">
                      {user.firstName?.[0]}
                      {user.lastName?.[0]}
                    </div>
                    <div className="week-user-meta">
                      <span className="week-user-name">
                        {user.firstName} {user.lastName}
                      </span>
                      <span className="week-user-role">
                        {getUserRoleLabel(user)}
                      </span>
                    </div>
                  </td>
                  {weekDays.map((d) => {
                    const dateStr = formatLocalDate(d);
                    const dayData = user.days?.[dateStr];
                    return (
                      <td
                        key={dateStr}
                        className={`week-task-cell ${
                          dayData?.type?.toLowerCase() || "working"
                        }`}
                        onDoubleClick={() => onDayDoubleClick(dateStr)}
                        onContextMenu={(e) =>
                          handleContextMenu(e, dateStr, dayData, user)
                        }
                      >
                        {dayData?.type === "HOLIDAY" && (
                          <span className="cell-holiday-mini">
                            {dayData.holidayName}
                          </span>
                        )}
                        {dayData?.type === "WEEKLY_OFF" && (
                          <span className="cell-off-mini">Weekly Off</span>
                        )}
                        {dayData?.type === "OFF" && (
                          <span className="cell-off-mini">
                            {dayData?.overrideNote || "Leave"}
                          </span>
                        )}
                        {dayData?.type === "HALF_DAY" && (
                          <span className="cell-off-mini">Half day</span>
                        )}
                        {dayData?.type === "WORKING" &&
                          dayData.tasks?.length === 0 && (
                            <span className="cell-free-mini">Free</span>
                          )}
                        {dayData?.type === "WORKING" &&
                          dayData.tasks?.map((task) => {
                            const statusStyle = getStatusStyle(
                              task.status,
                              task.slaPercentage,
                            );
                            return (
                              <div
                                key={task.id}
                                className="week-task-chip"
                                style={{
                                  backgroundColor: statusStyle.backgroundColor,
                                  borderLeft: `3px solid ${statusStyle.borderColor}`,
                                }}
                              >
                                <span className="week-chip-stage">
                                  {task.stageName}
                                </span>
                                <span className="chip-project">
                                  {task.projectName}
                                </span>
                              </div>
                            );
                          })}
                        {/* {dayData?.source === "USER_OVERRIDE" && (
                          <span
                            className="override-indicator"
                            title="Custom schedule"
                          >
                            {dayData.type === "OFF" && "??"}
                            {dayData.type === "HALF_DAY" && "?"}
                            {dayData.type === "WORKING" && "?"}
                          </span>
                        )} */}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {contextMenu && (
          <PlannerContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            dateStr={contextMenu.dateStr}
            dayInfo={contextMenu.dayInfo}
            user={contextMenu.user}
            isAdmin={isAdmin}
            onAction={handleContextAction}
            onClose={() => setContextMenu(null)}
          />
        )}

        {confirmModal && (
          <RecurringScheduleConfirmModal
            user={confirmModal.user}
            dateStr={confirmModal.dateStr}
            action={confirmModal.type}
            currentWorkingDays={confirmModal.currentWorkingDays}
            onConfirm={async ({ newWorkingDays, effectiveFrom }) => {
              await plannerAPI.updateUserSchedule(confirmModal.user.userId, {
                workingDays: newWorkingDays,
                workStartTime: confirmModal.user.workStartTime,
                workEndTime: confirmModal.user.workEndTime,
                effectiveFrom,
              });
              setConfirmModal(null);
              onRefresh();
            }}
            onClose={() => setConfirmModal(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="week-view single-week">
      <div className="week-columns">
        {weekDays.map((d, i) => {
          const dateStr = formatLocalDate(d);
          const dayData = data?.days?.[dateStr];
          const isToday = dateStr === today;

          return (
            <div
              key={dateStr}
              className={`week-day-column ${
                dayData?.type?.toLowerCase() || "working"
              } ${isToday ? "today" : ""}`}
              onDoubleClick={() => onDayDoubleClick(dateStr)}
              onContextMenu={(e) =>
                handleContextMenu(e, dateStr, dayData, data?.user)
              }
            >
              <div className="week-col-header">
                <span className="week-col-dayname">{DAY_NAMES[i]}</span>
                <span className="week-col-date">{d.getDate()}</span>
              </div>

              {dayData?.type === "HOLIDAY" && (
                <div className="col-holiday">{dayData.holidayName}</div>
              )}
              {dayData?.type === "WEEKLY_OFF" && (
                <div className="col-off">Weekly Off</div>
              )}
              {dayData?.type === "OFF" && (
                <div className="col-off">
                  {dayData?.overrideNote || "Leave"}
                </div>
              )}
              {dayData?.type === "HALF_DAY" && (
                <div className="col-off">Half Day</div>
              )}
              {dayData?.type === "WORKING" && (
                <div className="col-tasks">
                  {dayData.tasks?.length === 0 && (
                    <p className="col-empty">No tasks</p>
                  )}
                  {dayData.tasks?.map((task, idx) => {
                    const statusStyle = getStatusStyle(
                      task.status,
                      task.slaPercentage,
                    );
                    return (
                      <div
                        key={task.id}
                        className="col-task-card"
                        style={{
                          backgroundColor: statusStyle.backgroundColor,
                          borderLeft: `3px solid ${statusStyle.borderColor}`,
                        }}
                      >
                        <span className="col-task-pos">{idx + 1}</span>
                        <div>
                          <span className="col-task-stage">
                            {task.stageName}
                          </span>
                          <span className="col-task-project">
                            {task.projectName}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* {dayData?.source === "USER_OVERRIDE" && (
                <span className="override-indicator" title="Custom schedule">
                  {dayData.type === "OFF" && "??"}
                  {dayData.type === "HALF_DAY" && "?"}
                  {dayData.type === "WORKING" && "?"}
                </span>
              )} */}

              <div
                className="week-col-drill"
                title="Double-click to open day view"
              ></div>
            </div>
          );
        })}
      </div>

      {contextMenu && (
        <PlannerContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          dateStr={contextMenu.dateStr}
          dayInfo={contextMenu.dayInfo}
          user={contextMenu.user}
          isAdmin={isAdmin}
          onAction={handleContextAction}
          onClose={() => setContextMenu(null)}
        />
      )}

      {confirmModal && data?.user && (
        <RecurringScheduleConfirmModal
          user={confirmModal.user}
          dateStr={confirmModal.dateStr}
          action={confirmModal.type}
          currentWorkingDays={confirmModal.currentWorkingDays}
          onConfirm={async ({ newWorkingDays, effectiveFrom }) => {
            await plannerAPI.updateUserSchedule(confirmModal.user.id, {
              workingDays: newWorkingDays,
              workStartTime: data?.workStartTime,
              workEndTime: data?.workEndTime,
              effectiveFrom,
            });
            setConfirmModal(null);
            onRefresh();
          }}
          onClose={() => setConfirmModal(null)}
        />
      )}
    </div>
  );
}
